import { Accordion, Skeleton } from '@distribusion/owl-frontend-ui'
import cn from 'classnames'
import { useFormikContext } from 'formik'
import React, { useCallback, useContext, useMemo } from 'react'

import config from '@config'
import ancillaryUtils from '@lib/ancillary'
import bem from '@lib/bem'
import connectionUtils from '@lib/connection'
import { useTranslation } from '@lib/i18n'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { ConnectionSeats } from '@pages/Checkout/SeatSelection/NewSeats/ConnectionSeats'
import { EnabledContext } from '@pages/Checkout/SeatSelection/NewSeats/context/enabled'
import {
  SeatAncillariesContext,
  useSeatAncillaryContextValue,
} from '@pages/Checkout/SeatSelection/NewSeats/context/seatAncillary'
import { OptionalSeatSelectionToggle } from '@pages/Checkout/SeatSelection/NewSeats/OptionalSeatSelectionToggle'
import { Warning } from '@pages/Checkout/SeatSelection/NewSeats/Warning'
import { WithSeatsModal } from '@pages/Checkout/SeatSelection/NewSeats/WithSeatsModal'
import { SeatsProps } from '@pages/Checkout/SeatSelection/Seats'
import { FieldsetContext } from '@pages/context/fieldset'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'
import { useParams } from '@stores/params'

import '@pages/Checkout/SeatSelection/NewSeats/index.scss'

const SEATS_CLASSES = cn('new-seats', 'column')

const updateSegments = (ancillaries: Ancillary.Item[], connectionSegments: Segment[]): Ancillary.Item[] =>
  ancillaries.map(item => ({
    ...item,
    segments:
      item.segments?.map(segment => ({
        ...segment,
        index: connectionSegments.findIndex(connection => connectionUtils.isSameSegment(connection, segment)),
      })) ?? [],
  }))

const Seats: React.FC<SeatsProps> = ({ enabled: externallyEnabled = true, isConnectionsLoading, ...props }) => {
  const { t } = useTranslation()
  const [{ outbound, inbound }] = useCheckout()
  const {
    values: { vacancy, isVacancyLoading, fareClass, returnFareClass },
    errors: { seats: seatsErrors },
    touched: { seats: seatsTouched },
  } = useFormikContext<CheckoutFormData>()
  const [
    {
      seatSelection: { enabledOnCheckout },
    },
  ] = useSettings()
  const [{ marketingCarrierCode }] = useParams()
  const isAncillary = marketingCarrierCode ? config.seatAncillaryAsSelection?.[marketingCarrierCode] : false
  const ancillaries = useMemo(() => {
    const connectionSegments = [outbound, inbound].flatMap(connection => connection?.segments).filter(Boolean)
    const seatAncillaries = ancillaryUtils.getByCategory('SEAT', vacancy?.ancillaries)

    return updateSegments(seatAncillaries, connectionSegments as Segment[])
  }, [inbound, outbound, vacancy?.ancillaries])
  const ancillaryContextValue = useSeatAncillaryContextValue(ancillaries)
  const { isDisabled } = useContext(FieldsetContext)
  const globalAncillaries = ancillaryContextValue.globalAncillaries
  const optionalGlobalAncillaries = globalAncillaries.filter(ancillary => !ancillaryUtils.isMandatory(ancillary))
  const showOptionalToggle = isAncillary && optionalGlobalAncillaries.length
  const { isAncillaryActive, activateAncillary, deactivateAncillary } = ancillaryContextValue
  const enabled = Boolean(
    externallyEnabled && !isDisabled && (!showOptionalToggle || globalAncillaries.some(isAncillaryActive)),
  )

  const handleGlobalToggleChange = useCallback(
    (active: boolean) =>
      optionalGlobalAncillaries.forEach(ancillary => {
        active ? activateAncillary(ancillary) : deactivateAncillary(ancillary)
      }),
    [optionalGlobalAncillaries, activateAncillary, deactivateAncillary],
  )

  return (
    <SeatAncillariesContext.Provider value={ancillaryContextValue}>
      <WithSeatsModal enabled={externallyEnabled && !!(enabledOnCheckout && vacancy?.vacant)} {...props}>
        {isSeatsLoading => (
          <Skeleton.List
            Skeleton={() => (
              <div className={cn(bem('new-seats', 'skeleton'), 'column', 'py-4')}>
                <Skeleton.Item height={24} />
                <Skeleton.Item height={20} />
              </div>
            )}
            amount={1}
            loading={isSeatsLoading || isConnectionsLoading || isVacancyLoading || !outbound}
          >
            {() => (
              <EnabledContext.Provider value={{ enabled }}>
                <div data-tag="new-seats" className={SEATS_CLASSES}>
                  {showOptionalToggle ? (
                    <OptionalSeatSelectionToggle
                      context="all"
                      ancillaries={optionalGlobalAncillaries}
                      enabled={optionalGlobalAncillaries.some(isAncillaryActive)}
                      onChange={handleGlobalToggleChange}
                    />
                  ) : null}
                  {outbound && inbound ? (
                    <>
                      <Accordion
                        titleComponent="h5"
                        titleClassName="h4"
                        title={t('extras.seats.outbound')}
                        divider={false}
                        key={'outbound'}
                      >
                        <div className="pt-4">
                          <ConnectionSeats connection={outbound} fareClass={fareClass} direction="outbound" />
                        </div>
                      </Accordion>
                      <hr className="m-0" />
                      <Accordion
                        titleComponent="h5"
                        titleClassName="h4"
                        title={t('extras.seats.inbound')}
                        divider={false}
                        key={'inbound'}
                      >
                        <div className="pt-4">
                          <ConnectionSeats connection={inbound} fareClass={returnFareClass!} direction="inbound" />
                        </div>
                      </Accordion>
                    </>
                  ) : (
                    <ConnectionSeats connection={outbound!} fareClass={fareClass} direction="outbound" />
                  )}
                  {seatsTouched && seatsErrors && <Warning>{seatsErrors}</Warning>}
                </div>
              </EnabledContext.Provider>
            )}
          </Skeleton.List>
        )}
      </WithSeatsModal>
    </SeatAncillariesContext.Provider>
  )
}

export default Seats
